// Clinical reports (ClinicalReports.view — the Compliance Manager): every report,
// filtered on the page, opened in components/ClinicalReportDialog.jsx to be worked.
// The filters are clinicalReportRules.js; the export takes the rows exactly as
// filtered.
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box, Button, Chip, CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem,
  Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import { API_BASE_URL } from "../config";
import {
  CLINICAL_HARM_COLOR, CLINICAL_REPORT_STATUSES, CLINICAL_REPORT_STATUS_COLOR,
  CLINICAL_REPORT_TYPES,
} from "../constants";
import {
  NOTIFICATION_FILTERS, NO_EVENT, affectedText, emptyReportFilters, filterReports,
  notificationCounts, reportTypeText,
} from "../clinicalReportRules";
import { exportClinicalReports } from "../exportExcel";
import { useMe } from "../MeContext";
import ClinicalReportDialog from "../components/ClinicalReportDialog";

const API = `${API_BASE_URL}/clinical-reports`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

function NotificationsCell({ report }) {
  const { overdue, outstanding } = notificationCounts(report);
  const total = (report.notifications || []).length;
  if (!total) return <Typography variant="body2" color="text.secondary">—</Typography>;
  if (overdue) return <Chip size="small" color="error" label={`${overdue} overdue`} />;
  if (outstanding) return <Chip size="small" color="warning" label={`${outstanding} due`} />;
  return <Chip size="small" color="success" variant="outlined" label="All dealt with" />;
}

export default function ClinicalReports() {
  const { can } = useMe();
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState(emptyReportFilters);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/`).then(res => setRows(res.data)).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  // The events that have reports, most recent report first.
  const events = useMemo(() => {
    const seen = new Map();
    for (const r of rows || []) {
      if (r.event_id != null && !seen.has(r.event_id)) seen.set(r.event_id, r.event_label);
    }
    return [...seen.entries()];
  }, [rows]);

  const shown = useMemo(() => filterReports(rows || [], filters), [rows, filters]);
  const set = (key) => (e) => setFilters(f => ({ ...f, [key]: e.target.value }));
  const filtered = Object.values(filters).some(Boolean);

  const saved = (r) => setRows(rs => (rs.some(x => x.id === r.id)
    ? rs.map(x => (x.id === r.id ? r : x)) : [r, ...rs]));

  const select = (key, label, options, width = 170) => (
    <FormControl size="small" sx={{ minWidth: width }}>
      <InputLabel id={`crf-${key}`}>{label}</InputLabel>
      <Select labelId={`crf-${key}`} label={label} value={filters[key]} onChange={set(key)}>
        <MenuItem value=""><em>Any</em></MenuItem>
        {options.map(([value, text]) => <MenuItem key={value} value={value}>{text}</MenuItem>)}
      </Select>
    </FormControl>
  );

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="h4" sx={{ flex: 1 }}>Clinical reports</Typography>
        {can("ClinicalReports.edit") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenId("new")}>
            New report
          </Button>
        )}
        <Button variant="outlined" startIcon={<FileDownloadIcon />} disabled={!shown.length}
                onClick={() => exportClinicalReports(shown)}>
          Export to Excel
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        {select("event", "Event",
          [...events.map(([id, label]) => [String(id), label]), [NO_EVENT, "Not at an event"]], 220)}
        {select("type", "Type", CLINICAL_REPORT_TYPES.map(t => [t.value, t.value]), 240)}
        {select("status", "Status", CLINICAL_REPORT_STATUSES.map(s => [s, s]))}
        {select("notifications", "Notifications", NOTIFICATION_FILTERS)}
        <TextField size="small" label="Search names" value={filters.search} onChange={set("search")}
                   slotProps={{ input: { startAdornment: (
                     <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) } }}
                   helperText="The person affected" sx={{ minWidth: 220 }} />
        {filtered && <Button size="small" onClick={() => setFilters(emptyReportFilters())}>Clear</Button>}
        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
          {rows ? `${shown.length} of ${rows.length}` : ""}
        </Typography>
      </Box>

      {rows == null ? <CircularProgress /> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ref</TableCell>
                <TableCell>Happened</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Person affected</TableCell>
                <TableCell>Harm</TableCell>
                <TableCell>Notifications</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary">
                      {rows.length ? "Nothing matches these filters." : "No clinical reports yet."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {shown.map(r => (
                <TableRow hover key={r.id} onClick={() => setOpenId(r.id)} sx={{ cursor: "pointer" }}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{r.reference}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(r.occurred_at || r.created_at)}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{reportTypeText(r)}</TableCell>
                  <TableCell>{r.event_label || <em>Not at an event</em>}</TableCell>
                  <TableCell>{affectedText(r)}</TableCell>
                  <TableCell>
                    {r.harm ? <Chip size="small" label={r.harm} color={CLINICAL_HARM_COLOR[r.harm] || "default"} />
                      : <Typography variant="body2" color="text.secondary">Not assessed</Typography>}
                  </TableCell>
                  <TableCell><NotificationsCell report={r} /></TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} color={CLINICAL_REPORT_STATUS_COLOR[r.status] || "default"} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ClinicalReportDialog reportId={openId} onClose={() => setOpenId(null)} onSaved={saved}
                            onDeleted={(id) => setRows(rs => rs.filter(r => r.id !== id))} />
    </Stack>
  );
}
